import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Pages
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Products from "@/pages/Products";
import Courses from "@/pages/Courses";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogDetail from "@/pages/BlogDetail";
import TrainingGallery from "@/pages/TrainingGallery";

// Admin Pages
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminLeads from "@/pages/admin/Leads";
import AdminServices from "@/pages/admin/Services";
import AdminCourses from "@/pages/admin/Courses";
import AdminProducts from "@/pages/admin/Products";
import AdminTestimonials from "@/pages/admin/Testimonials";
import AdminBlogs from "@/pages/admin/Blogs";
import AdminPartners from "@/pages/admin/Partners";
import AdminBrochures from "@/pages/admin/Brochures";
import AdminAnnouncements from "@/pages/admin/Announcements";
import AdminEMRPlans from "@/pages/admin/EMRPlans";
import AdminTrainingGallery from "@/pages/admin/TrainingGallery";
import AdminAcademicPartners from "@/pages/admin/AcademicPartners";
import AdminSettings from "@/pages/admin/Settings";

// Components
import Layout from "@/components/Layout";
import AdminLayout from "@/components/AdminLayout";
import ProtectedRoute from "@/components/ProtectedRoute";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function App() {
  useEffect(() => {
    // Seed initial data
    const seedData = async () => {
      try {
        await axios.post(`${API_URL}/seed`);
      } catch (e) {
        console.log("Seed skipped or already done");
      }
    };
    seedData();

    // Load Google Analytics dynamically from settings
    const loadGA = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings`);
        const gaId = res.data?.google_analytics_id;
        if (gaId && gaId !== '' && !gaId.includes('XXXX')) {
          const script = document.createElement('script');
          script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
          script.async = true;
          document.head.appendChild(script);
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', gaId);
        }
      } catch (e) {
        console.log("GA load skipped");
      }
    };
    loadGA();
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/about" element={<Layout><About /></Layout>} />
            <Route path="/services" element={<Layout><Services /></Layout>} />
            <Route path="/services/:slug" element={<Layout><ServiceDetail /></Layout>} />
            <Route path="/products" element={<Layout><Products /></Layout>} />
            <Route path="/courses" element={<Layout><Courses /></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
            <Route path="/blog" element={<Layout><Blog /></Layout>} />
            <Route path="/blog/:slug" element={<Layout><BlogDetail /></Layout>} />
            <Route path="/gallery" element={<Layout><TrainingGallery /></Layout>} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/leads" element={<ProtectedRoute><AdminLayout><AdminLeads /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/services" element={<ProtectedRoute><AdminLayout><AdminServices /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/courses" element={<ProtectedRoute><AdminLayout><AdminCourses /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/products" element={<ProtectedRoute><AdminLayout><AdminProducts /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/testimonials" element={<ProtectedRoute><AdminLayout><AdminTestimonials /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/blogs" element={<ProtectedRoute><AdminLayout><AdminBlogs /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/partners" element={<ProtectedRoute><AdminLayout><AdminPartners /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/brochures" element={<ProtectedRoute><AdminLayout><AdminBrochures /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/announcements" element={<ProtectedRoute><AdminLayout><AdminAnnouncements /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/emr-plans" element={<ProtectedRoute><AdminLayout><AdminEMRPlans /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/training-gallery" element={<ProtectedRoute><AdminLayout><AdminTrainingGallery /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/academic-partners" element={<ProtectedRoute><AdminLayout><AdminAcademicPartners /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><AdminLayout><AdminSettings /></AdminLayout></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </HelmetProvider>
  );
}

export default App;
